/**
 * ONE key pipeline, with layers.
 *
 * Every navigation key in the app — from a browser keydown or from the
 * Android wrapper's dispatchKeyEvent bridge — arrives here and is offered to
 * the layers top-down; the first one that returns true consumes it.
 *
 * Why a stack rather than "the focus system handles keys":
 *
 * Spatial navigation is the right answer on a grid and the wrong answer over
 * full-screen video, where left/right mean previous/next channel and up means
 * "show me what I was just watching". Without a layer, that becomes a growing
 * pile of `if (view === 'theater')` checks inside the focus system, which then
 * has to know about playback, recents, and every dialog we ever add.
 *
 * With a layer, the player pushes its own handling while it is on screen and
 * pops it when it leaves. The focus system stays the BASE layer and knows
 * nothing about any of it.
 */

/**
 * `menu` is the remote's context key.
 *
 * A television has no right-click and no long-press, so acting on the thing
 * under the cursor — star it, hide it — needs a key of its own. Every remote
 * has one; the wrapper forwards it here, and falls back to its own server
 * settings dialog only when no layer wants it.
 */
export type NavKey = 'up' | 'down' | 'left' | 'right' | 'select' | 'back' | 'menu';

type Layer = (key: NavKey) => boolean;

const layers: Layer[] = [];
const activityListeners = new Set<() => void>();

/**
 * Push a handler on top. Returns the function that removes it.
 *
 * `base: true` pins it to the bottom instead — spatial navigation is the
 * last resort by definition, and it must stay there even if it remounts
 * (signing out and back in) while another layer is live. Otherwise the
 * fallback would end up ABOVE the layer it is meant to fall back from, and
 * the bug would only appear on the one path nobody re-tests.
 */
export function pushKeyLayer(layer: Layer, opts?: { base?: boolean }): () => void {
  if (opts?.base) layers.unshift(layer);
  else layers.push(layer);
  return () => {
    const i = layers.indexOf(layer);
    if (i >= 0) layers.splice(i, 1);
  };
}

/**
 * "The user is here" — fires for EVERY key, whoever ends up consuming it.
 *
 * This exists because of a Fire TV detail that is invisible on a desktop: the
 * wrapper consumes D-pad keys in Kotlin and calls into the page directly, so
 * no DOM `keydown` is ever produced. Anything that listened for keydown to
 * decide the user was still there — the auto-hiding player chrome, for
 * instance — was therefore dead on the one device it exists for.
 */
export function onKeyActivity(fn: () => void): () => void {
  activityListeners.add(fn);
  return () => { activityListeners.delete(fn); };
}

export function dispatchKey(key: NavKey): boolean {
  for (const fn of activityListeners) fn();
  for (let i = layers.length - 1; i >= 0; i--) {
    if (layers[i](key)) return true;
  }
  return false;
}

export function keyFromEvent(e: KeyboardEvent): NavKey | null {
  switch (e.key) {
    case 'ArrowUp': return 'up';
    case 'ArrowDown': return 'down';
    case 'ArrowLeft': return 'left';
    case 'ArrowRight': return 'right';
    case 'Enter': return 'select';
    case 'Escape':
    case 'Backspace':
    case 'GoBack': return 'back';
    // A keyboard stand-in for the remote's context key, so the same menu can
    // be reached while developing in a browser.
    case 'ContextMenu':
    case 'm':
    case 'M': return 'menu';
    default: return null;
  }
}
